import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { getTenantSettings, setAiKeySource, type AiKeySource } from '../lib/assistant-settings-api.js';
import { errorMessage } from '../lib/properties.js';
import {
  getCreditBalance,
  getCreditLedger,
  getCreditUsage,
  getTopupConfig,
  createTopup,
  getProviderKey,
  saveProviderKey,
  removeProviderKey,
  type AiKeyProvider,
  type BillingDto,
} from '../lib/ai-credits-api.js';

/**
 * Krok KR1 — "Kdo platí AI" (vypnuto / vlastní klíč / kredity REOS), vlastní
 * API klíč a penezenka kreditu. Zije vedle `AssistantConnectorsCard` (AS2) -
 * ta resi KTERY model/konektor, tahle karta KDO PLATI (nezavisle pole
 * `ai_key_source`). Stejny vzor jako AS2: rh-card, rh-a2-* tokeny, zadne
 * nove komponenty z kitu.
 */

const QUERY_KEY_SETTINGS = ['nastaveni', 'assistant-provider'] as const;

/**
 * Zustatek a cerpani se NEZAOKROUHLUJI na cele koruny (`Math.trunc`) - u
 * malych volani (zlomky haléře na dotaz) by to v tabulce "na co" ukazovalo
 * "0 Kč" i pri realne nenulove spotrebe (zivy nalez pri overeni KR1). Castky
 * dobiti (vzdy cele Kc od uzivatele) zustavaji trunc - viz volani nize.
 */
function formatCreditsAmount(value: string): string {
  const n = Number(value);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(n < 1 && n > -1 ? 4 : 2);
}

function PaymentModeSelector(): React.JSX.Element {
  const t2 = useT();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: QUERY_KEY_SETTINGS, queryFn: getTenantSettings });
  const [savedNote, setSavedNote] = React.useState<string | null>(null);

  async function onChange(next: AiKeySource): Promise<void> {
    setSavedNote(null);
    try {
      await setAiKeySource(next);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY_SETTINGS });
      setSavedNote(t2('webAdmin.settings.aiPaymentModeSaved'));
    } catch (e) {
      setSavedNote(t2('webAdmin.settings.aiPaymentModeError', { error: errorMessage(e) }));
    }
  }

  const mode = settings.data?.aiKeySource ?? 'off';

  return (
    <div style={{ marginBottom: 16 }}>
      <span className="mb-1 block text-sm font-medium">{t2('webAdmin.settings.aiPaymentModeTitle')}</span>
      <div className="rh-a2-seg" role="radiogroup" aria-label={t2('webAdmin.settings.aiPaymentModeTitle')} data-testid="ai-payment-mode">
        {(['off', 'tenant_key', 'app_credits'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            className={mode === value ? 'is-on' : ''}
            data-testid={`ai-payment-mode-${value}`}
            disabled={settings.isLoading}
            onClick={() => void onChange(value)}
          >
            {t2(
              value === 'off'
                ? 'webAdmin.settings.aiPaymentModeOff'
                : value === 'tenant_key'
                  ? 'webAdmin.settings.aiPaymentModeTenantKey'
                  : 'webAdmin.settings.aiPaymentModeAppCredits',
            )}
          </button>
        ))}
      </div>
      {savedNote !== null ? <p className="mt-1 text-xs text-gray-600">{savedNote}</p> : null}
    </div>
  );
}

function OwnKeySection(): React.JSX.Element {
  const t2 = useT();
  const queryClient = useQueryClient();
  const keyQuery = useQuery({ queryKey: ['nastaveni', 'ai-provider-key'], queryFn: getProviderKey });
  const [provider, setProvider] = React.useState<AiKeyProvider>('anthropic');
  const [apiKey, setApiKey] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await saveProviderKey(provider, apiKey);
      setApiKey('');
      await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'ai-provider-key'] });
    } catch (e2) {
      setError(t2('webAdmin.settings.aiKeySaveError', { error: errorMessage(e2) }));
    } finally {
      setSaving(false);
    }
  }

  async function onRemove(): Promise<void> {
    if (!window.confirm(t2('webAdmin.settings.aiKeyRemoveConfirm'))) return;
    await removeProviderKey();
    await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'ai-provider-key'] });
  }

  const key = keyQuery.data?.key ?? null;

  return (
    <div data-testid="ai-key-section">
      <h3 className="mb-1 text-sm font-semibold">{t2('webAdmin.settings.aiKeyTitle')}</h3>
      <p className="mb-2 text-sm text-gray-600">{t2('webAdmin.settings.aiKeyPerex')}</p>
      {key !== null ? (
        <p className="mb-2 text-sm" data-testid="ai-key-status">
          {key.status === 'ok'
            ? t2('webAdmin.settings.aiKeyStatusOk', { hint: key.secretHint })
            : t2('webAdmin.settings.aiKeyStatusInvalid', { hint: key.secretHint })}{' '}
          <button type="button" className="rh-a2-link text-xs" onClick={() => void onRemove()} data-testid="ai-key-remove">
            {t2('webAdmin.settings.aiKeyRemoveButton')}
          </button>
        </p>
      ) : null}
      <form onSubmit={(e) => void onSave(e)} className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium" htmlFor="ai-key-provider">
            {t2('webAdmin.settings.aiKeyProviderLabel')}
          </label>
          <select
            id="ai-key-provider"
            data-testid="ai-key-provider"
            className="rounded border px-2 py-1 text-sm"
            value={provider}
            onChange={(e) => setProvider(e.target.value as AiKeyProvider)}
          >
            <option value="anthropic">{t2('webAdmin.settings.aiKeyProviderAnthropic')}</option>
            <option value="openai">{t2('webAdmin.settings.aiKeyProviderOpenai')}</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label className="mb-1 block text-xs font-medium" htmlFor="ai-key-input">
            {t2('webAdmin.settings.aiKeyInputLabel')}
          </label>
          <input
            id="ai-key-input"
            data-testid="ai-key-input"
            type="password"
            className="w-full rounded border px-2 py-1 text-sm"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required
            minLength={10}
          />
        </div>
        <button type="submit" className="rh-a2-link text-sm" disabled={saving || apiKey.trim() === ''} data-testid="ai-key-save">
          {saving ? t2('webAdmin.settings.aiKeySaving') : t2('webAdmin.settings.aiKeySaveButton')}
        </button>
      </form>
      {error !== null ? (
        <p className="mt-1 text-xs text-red-600" data-testid="ai-key-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Jednoduchý sloupcový graf (jedna řada = sekvenční odstín, bez legendy - viz dataviz skill). */
function UsageChart({ daily }: { readonly daily: readonly { day: string; spentCzk: string }[] }): React.JSX.Element {
  const t2 = useT();
  if (daily.length === 0) {
    return <p className="text-sm text-gray-600">{t2('webAdmin.settings.aiCreditsUsageChartEmpty')}</p>;
  }
  const max = Math.max(...daily.map((d) => Number(d.spentCzk)), 0.0001);
  const width = 320;
  const height = 64;
  const barWidth = width / daily.length;
  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={t2('webAdmin.settings.aiCreditsUsageChartLabel')}
        data-testid="ai-usage-chart"
      >
        {daily.map((d, i) => {
          const h = Math.max(1, (Number(d.spentCzk) / max) * (height - 4));
          return (
            <rect
              key={d.day}
              x={i * barWidth + 1}
              y={height - h}
              width={Math.max(1, barWidth - 2)}
              height={h}
              rx={2}
              fill="var(--rh-accent)"
            >
              <title>{`${d.day}: ${d.spentCzk} Kč`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="sr-only">
        <table>
          <caption>{t2('webAdmin.settings.aiCreditsUsageChartLabel')}</caption>
          <tbody>
            {daily.map((d) => (
              <tr key={d.day}>
                <td>{d.day}</td>
                <td>{d.spentCzk}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </figcaption>
    </figure>
  );
}

const EMPTY_BILLING: BillingDto = { name: '' };

function TopupSection(): React.JSX.Element {
  const t2 = useT();
  const queryClient = useQueryClient();
  const config = useQuery({ queryKey: ['nastaveni', 'ai-topup-config'], queryFn: getTopupConfig });
  const [method, setMethod] = React.useState<'card' | 'bank_transfer'>('card');
  const [amount, setAmount] = React.useState('500.0000');
  const [email, setEmail] = React.useState('');
  const [billing, setBilling] = React.useState<BillingDto>(EMPTY_BILLING);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [bankResult, setBankResult] = React.useState<string | null>(null);

  function field(key: keyof BillingDto): [string, (v: string) => void] {
    return [billing[key] ?? '', (v: string) => setBilling((b) => ({ ...b, [key]: v }))];
  }
  const [nameVal, setName] = field('name');
  const [icoVal, setIco] = field('ico');
  const [vatIdVal, setVatId] = field('vatId');
  const [streetVal, setStreet] = field('street');
  const [cityVal, setCity] = field('city');
  const [zipVal, setZip] = field('zip');
  const [countryVal, setCountry] = field('country');

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setBankResult(null);
    try {
      const result = await createTopup({ method, amountCzkNet: amount, email, billing });
      if (result.method === 'card') {
        window.location.href = result.redirectUrl;
        return;
      }
      setBankResult(
        t2('webAdmin.settings.aiCreditsTopupBankTransferResult', {
          vs: result.variableSymbol,
          amount: result.amountCzkGross,
          due: new Date(result.dueAt).toLocaleDateString('cs-CZ'),
        }),
      );
      await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'ai-credits-balance'] });
    } catch (e2) {
      setError(t2('webAdmin.settings.aiCreditsTopupError', { error: errorMessage(e2) }));
    } finally {
      setSubmitting(false);
    }
  }

  const quickAmounts = config.data?.quickAmountsCzk ?? [];

  return (
    <div data-testid="ai-topup-section">
      <h3 className="mb-1 text-sm font-semibold">{t2('webAdmin.settings.aiCreditsTopupTitle')}</h3>
      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="mb-2">
          <span className="mb-1 block text-xs font-medium">{t2('webAdmin.settings.aiCreditsTopupAmountLabel')}</span>
          <div className="flex flex-wrap gap-2">
            {quickAmounts.map((a) => (
              <button
                key={a}
                type="button"
                className={amount === a ? 'rh-a2-link is-on text-sm' : 'rh-a2-link text-sm'}
                data-testid={`ai-topup-quick-${a}`}
                onClick={() => setAmount(a)}
              >
                {Math.trunc(Number(a))} {t2('webAdmin.settings.aiCreditsBalanceUnit')}
              </button>
            ))}
            <input
              type="number"
              min={200}
              step={1}
              aria-label={t2('webAdmin.settings.aiCreditsTopupCustomLabel')}
              data-testid="ai-topup-custom-amount"
              className="w-32 rounded border px-2 py-1 text-sm"
              value={Math.trunc(Number(amount))}
              onChange={(e) => setAmount(`${e.target.value}.0000`)}
            />
          </div>
        </div>
        <div className="mb-2" role="radiogroup" aria-label={t2('webAdmin.settings.aiCreditsTopupMethodLabel')}>
          <span className="mb-1 block text-xs font-medium">{t2('webAdmin.settings.aiCreditsTopupMethodLabel')}</span>
          <label className="mr-3 text-sm">
            <input type="radio" name="ai-topup-method" checked={method === 'card'} onChange={() => setMethod('card')} />{' '}
            {t2('webAdmin.settings.aiCreditsTopupMethodCard')}
          </label>
          <label className="text-sm">
            <input
              type="radio"
              name="ai-topup-method"
              checked={method === 'bank_transfer'}
              onChange={() => setMethod('bank_transfer')}
              data-testid="ai-topup-method-bank"
            />{' '}
            {t2('webAdmin.settings.aiCreditsTopupMethodBankTransfer')}
          </label>
        </div>
        <div className="mb-2 grid grid-cols-2 gap-2" style={{ maxWidth: 480 }}>
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupEmailLabel')} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="col-span-2 rounded border px-2 py-1 text-sm" data-testid="ai-topup-email" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingNameLabel')} required value={nameVal} onChange={(e) => setName(e.target.value)} className="col-span-2 rounded border px-2 py-1 text-sm" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingIcoLabel')} value={icoVal} onChange={(e) => setIco(e.target.value)} className="rounded border px-2 py-1 text-sm" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingVatIdLabel')} value={vatIdVal} onChange={(e) => setVatId(e.target.value)} className="rounded border px-2 py-1 text-sm" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingStreetLabel')} value={streetVal} onChange={(e) => setStreet(e.target.value)} className="col-span-2 rounded border px-2 py-1 text-sm" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingCityLabel')} value={cityVal} onChange={(e) => setCity(e.target.value)} className="rounded border px-2 py-1 text-sm" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingZipLabel')} value={zipVal} onChange={(e) => setZip(e.target.value)} className="rounded border px-2 py-1 text-sm" />
          <input placeholder={t2('webAdmin.settings.aiCreditsTopupBillingCountryLabel')} value={countryVal} onChange={(e) => setCountry(e.target.value)} className="rounded border px-2 py-1 text-sm" />
        </div>
        <button type="submit" className="rh-a2-link text-sm" disabled={submitting} data-testid="ai-topup-submit">
          {submitting ? t2('webAdmin.settings.aiCreditsTopupSubmitting') : t2('webAdmin.settings.aiCreditsTopupSubmitButton')}
        </button>
      </form>
      {bankResult !== null ? (
        <p className="mt-2 text-sm" data-testid="ai-topup-bank-result">
          {bankResult}
        </p>
      ) : null}
      {error !== null ? (
        <p className="mt-2 text-xs text-red-600" data-testid="ai-topup-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function CreditsSection(): React.JSX.Element {
  const t2 = useT();
  const balance = useQuery({ queryKey: ['nastaveni', 'ai-credits-balance'], queryFn: getCreditBalance });
  const usage = useQuery({ queryKey: ['nastaveni', 'ai-credits-usage'], queryFn: getCreditUsage });
  const ledger = useQuery({ queryKey: ['nastaveni', 'ai-credits-ledger'], queryFn: getCreditLedger });

  /*
   * Krok PIL4 bod 3 — SEZNAM ADMINU JE NA 390 PX KARTA, NE TABULKA.
   *
   * Průchod celou aplikací našel 6. 10. na `/nastaveni` sloupec „Typ" široký
   * 36 px (minimum je 48). Pravidlo „řádek = karta" je jedno a platí na
   * `.rh-a2-tbl` i `.rh-table` (krok FUNK1 bod 2) — tyhle dvě tabulky ho jen
   * neměly nasazené; FUNK1 je nechal v Nálezech jako úklid, protože tehdy
   * ještě na 390 px měřily ≥ 58 px. Popisek sloupce je JEDEN řetězec pro
   * hlavičku i pro `data-label`, aby se obě hodnoty nemohly rozejít.
   */
  const usageColumns = [
    t2('webAdmin.settings.aiCreditsUsageTableFunction'),
    t2('webAdmin.settings.aiCreditsUsageTableCalls'),
    t2('webAdmin.settings.aiCreditsUsageTableSpent'),
  ] as const;
  const ledgerColumns = [
    t2('webAdmin.settings.aiCreditsLedgerColumnDate'),
    t2('webAdmin.settings.aiCreditsLedgerColumnKind'),
    t2('webAdmin.settings.aiCreditsLedgerColumnAmount'),
    t2('webAdmin.settings.aiCreditsLedgerColumnReason'),
  ] as const;

  return (
    <div data-testid="ai-credits-section">
      <h3 className="mb-1 text-sm font-semibold">{t2('webAdmin.settings.aiCreditsTitle')}</h3>
      <p className="mb-3 text-2xl font-semibold" data-testid="ai-credits-balance">
        {balance.data?.balanceCzk !== undefined ? formatCreditsAmount(balance.data.balanceCzk) : '…'} {t2('webAdmin.settings.aiCreditsBalanceUnit')}
      </p>

      <TopupSection />

      <h4 className="mb-1 mt-4 text-sm font-semibold">{t2('webAdmin.settings.aiCreditsUsageTitle')}</h4>
      <UsageChart daily={usage.data?.daily ?? []} />

      <table className="rh-a2-tbl mt-3 w-full text-sm" data-testid="ai-usage-table">
        <caption className="sr-only">{t2('webAdmin.settings.aiCreditsUsageTableTitle')}</caption>
        <thead>
          <tr className="text-left text-xs text-gray-600">
            {usageColumns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(usage.data?.breakdown ?? []).map((row) => (
            <tr key={row.referenceType}>
              <td data-label={usageColumns[0]}>{row.referenceType}</td>
              <td data-label={usageColumns[1]}>{row.calls}</td>
              <td data-label={usageColumns[2]}>{formatCreditsAmount(row.spentCzk)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4 className="mb-1 mt-4 text-sm font-semibold">{t2('webAdmin.settings.aiCreditsLedgerTitle')}</h4>
      {(ledger.data?.items ?? []).length === 0 ? (
        <p className="text-sm text-gray-600">{t2('webAdmin.settings.aiCreditsLedgerEmpty')}</p>
      ) : (
        <table className="rh-a2-tbl w-full text-sm" data-testid="ai-ledger-table">
          <thead>
            <tr className="text-left text-xs text-gray-600">
              {ledgerColumns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(ledger.data?.items ?? []).map((row) => (
              <tr key={row.id}>
                <td data-label={ledgerColumns[0]}>{new Date(row.createdAt).toLocaleDateString('cs-CZ')}</td>
                <td data-label={ledgerColumns[1]}>{row.kind}</td>
                <td data-label={ledgerColumns[2]}>{row.amountCzk}</td>
                <td data-label={ledgerColumns[3]}>{row.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function AiWalletCard(): React.JSX.Element {
  const settings = useQuery({ queryKey: QUERY_KEY_SETTINGS, queryFn: getTenantSettings });
  const mode = settings.data?.aiKeySource ?? 'off';

  return (
    <div className="rh-card" style={{ maxWidth: 640, padding: 16, marginBottom: 16 }} data-testid="nastaveni-ai-wallet-card">
      <PaymentModeSelector />
      {mode === 'tenant_key' ? <OwnKeySection /> : null}
      {mode === 'app_credits' ? <CreditsSection /> : null}
    </div>
  );
}
